import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Plus } from 'lucide-react';
import type { EnrolmentPackageKind, PackageCancelSettlement, PatientPackageView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, money, newIdempotencyKey } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Select, Spinner, StatusBadge, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { packageKindLabel, packageKindVariant, type ServicePackage } from './PackagesTab';
import { AllowanceUse } from './PackageLineBadge';

export const PATIENT_PACKAGES_KEY = 'patient-packages';

type PackageScope = { patientId: string; admissionId?: string | null; appointmentId?: string | null };

export function packageStatusTone(status: string): 'success' | 'info' | 'muted' | 'destructive' {
  if (status === 'active') return 'success';
  if (status === 'consumed') return 'info';
  if (status === 'cancelled') return 'destructive';
  return 'muted';
}

export function usePatientPackages(scope: PackageScope, enabled = true) {
  const filter = scope.admissionId ? { admissionId: scope.admissionId } : scope.appointmentId ? { appointmentId: scope.appointmentId } : { patientId: scope.patientId };
  return useQuery({
    queryKey: [PATIENT_PACKAGES_KEY, filter],
    queryFn: () => get<{ data: PatientPackageView[]; nextCursor: string | null }>(`/patient-packages${qs({ ...filter, limit: 50 })}`),
    enabled,
  });
}

export function PatientPackagesCard({
  patientId,
  admissionId,
  appointmentId,
  sellKind,
  branchId,
  title,
  onChanged,
}: PackageScope & {
  sellKind?: EnrolmentPackageKind;
  branchId?: string | null;
  title?: string;
  onChanged?: () => void;
}) {
  const { can, hasModule } = useAuth();
  const queryClient = useQueryClient();
  const [selling, setSelling] = useState(false);
  const [cancelling, setCancelling] = useState<PatientPackageView | null>(null);
  const enabled = hasModule('billing');
  const packages = usePatientPackages({ patientId, admissionId, appointmentId }, enabled);
  if (!enabled) return null;

  const mayCancel = can('invoice.credit_note', 'invoice.line.remove', 'invoice.cancel');
  const maySell = Boolean(sellKind) && can('invoice.create');
  const changed = () => {
    void queryClient.invalidateQueries({ queryKey: [PATIENT_PACKAGES_KEY] });
    void queryClient.invalidateQueries({ queryKey: ['invoice'] });
    onChanged?.();
  };
  const rows = packages.data?.data ?? [];

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-sm">{title ?? tr('billing', 'packages')}</CardTitle>
        {maySell && (
          <Button size="sm" variant="outline" onClick={() => setSelling(true)}>
            <Plus className="h-4 w-4" />
            {sellKind === 'ipd_stay' ? tr('billing', 'attach_package') : tr('billing', 'sell_package')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {packages.isLoading && <Spinner />}
        {packages.isError && <Alert variant="destructive">{describeError(packages.error)}</Alert>}
        {!packages.isLoading && rows.length === 0 && <p className="text-sm text-muted-foreground">{tr('billing', 'no_packages_for_this_patient')}</p>}
        {rows.map((row) => (
          <PatientPackageRow key={row.id} row={row} onCancel={mayCancel && row.status !== 'cancelled' ? () => setCancelling(row) : undefined} />
        ))}
      </CardContent>
      {selling && sellKind && (
        <SellPackageDialog
          patientId={patientId}
          kind={sellKind}
          admissionId={admissionId ?? null}
          appointmentId={appointmentId ?? null}
          branchId={branchId ?? null}
          onClose={() => setSelling(false)}
          onSold={() => {
            setSelling(false);
            changed();
          }}
        />
      )}
      {cancelling && (
        <CancelPackageDialog
          row={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setCancelling(null);
            changed();
          }}
        />
      )}
    </Card>
  );
}

export function PatientPackageRow({ row, onCancel }: { row: PatientPackageView; onCancel?: () => void }) {
  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Package className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span className="font-medium">{row.name}</span>
        <Badge variant={packageKindVariant(row.kind)}>{packageKindLabel(row.kind)}</Badge>
        <StatusBadge status={row.status} label={tr('billing', `package_status_${row.status}`)} tone={packageStatusTone(row.status)} />
        <span className="ms-auto tabular text-sm font-medium">{money(row.saleNetMinor)}</span>
      </div>
      <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>{row.expiresAt ? tr('billing', 'valid_until_x', { date: dateOnly(row.expiresAt) }) : tr('billing', 'for_this_admission')}</span>
        {row.invoiceId && (
          <Link className="text-primary underline-offset-2 hover:underline" to={`/billing/invoices/${row.invoiceId}`}>
            {tr('billing', 'sold_on_bill_x', { number: row.invoiceNumber ?? '' })}
          </Link>
        )}
        {row.consumedValueMinor > 0 && <span>{tr('billing', 'used_worth_x', { amount: money(row.consumedValueMinor) })}</span>}
      </p>
      <ul className="space-y-1 text-sm">
        {row.items.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-2">
            <span className="truncate">{item.description}</span>
            <span className="shrink-0 tabular text-xs text-muted-foreground">
              {tr('billing', 'used_x_of_y_left_z', { used: item.usedQty, included: item.includedQty, left: item.remainingQty })}
            </span>
          </li>
        ))}
      </ul>
      <AllowanceUse allowanceMinor={row.allowanceMinor} usedMinor={row.allowanceUsedMinor} />
      {row.status === 'cancelled' && (
        <p className="text-xs text-muted-foreground">
          {row.cancelledReason}
          {row.refundMinor !== null && row.refundMinor > 0
            ? ` · ${row.creditNoteStatus === 'requested' ? tr('billing', 'credit_x_waiting_for_approval', { amount: money(row.refundMinor) }) : tr('billing', 'credited_x', { amount: money(row.refundMinor) })}`
            : ''}
        </p>
      )}
      {onCancel && (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" className="text-destructive" onClick={onCancel}>
            {tr('billing', 'cancel_package')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function SellPackageDialog({
  patientId,
  kind,
  admissionId,
  appointmentId,
  branchId,
  onClose,
  onSold,
}: {
  patientId: string;
  kind: EnrolmentPackageKind;
  admissionId: string | null;
  appointmentId: string | null;
  branchId: string | null;
  onClose: () => void;
  onSold: (sold: PatientPackageView) => void;
}) {
  const toast = useToast();
  const [packageId, setPackageId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const packages = useQuery({
    queryKey: ['packages', kind, 'active'],
    queryFn: () => get<ServicePackage[]>(`/packages${qs({ kind, active: 1 })}`),
  });
  const chosen = packages.data?.find((pkg) => pkg.id === packageId) ?? null;
  const sell = useMutation({
    mutationFn: () =>
      post<PatientPackageView>(
        '/patient-packages',
        {
          packageId,
          patientId,
          ...(admissionId ? { admissionId } : {}),
          ...(appointmentId ? { appointmentId } : {}),
          ...(branchId ? { branchId } : {}),
        },
        { idempotencyKey },
      ),
    onSuccess: (sold) => {
      toast.success(tr('billing', 'package_sold'));
      onSold(sold);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={() => (sell.isPending ? undefined : onClose())}
      title={kind === 'ipd_stay' ? tr('billing', 'attach_package') : tr('billing', 'sell_package')}
      description={tr('billing', `package_kind_hint_${kind}`)}
      icon={Package}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={sell.isPending}>
            {tr('billing', 'cancel')}
          </Button>
          <Button type="button" disabled={!chosen || sell.isPending} onClick={() => sell.mutate()}>
            {sell.isPending ? <Spinner /> : null}
            {tr('billing', 'sell_and_bill')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={tr('billing', 'package')} required>
          <Select value={packageId} onChange={(event) => setPackageId(event.target.value)}>
            <option value="">{packages.isLoading ? '…' : tr('billing', 'choose_a_package')}</option>
            {(packages.data ?? []).map((pkg) => (
              <option key={pkg.id} value={pkg.id}>
                {pkg.name} · {money(pkg.priceMinor)}
              </option>
            ))}
          </Select>
        </Field>
        {packages.data && packages.data.length === 0 && <Alert variant="warning">{tr('billing', 'no_packages_of_this_kind')}</Alert>}
        {chosen && (
          <div className="space-y-1 rounded-md bg-muted/50 p-3 text-sm">
            {chosen.validityDays && <p className="text-xs text-muted-foreground">{tr('billing', 'valid_for_x_days', { count: chosen.validityDays })}</p>}
            {chosen.allowanceMinor ? (
              <p className="text-xs text-muted-foreground">{tr('billing', 'medicines_up_to_x', { amount: money(chosen.allowanceMinor) })}</p>
            ) : null}
            <ul className="space-y-1">
              {chosen.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-2">
                  <span className="truncate">{item.chargeItem.name}</span>
                  <span className="tabular text-muted-foreground">× {item.qty}</span>
                </li>
              ))}
            </ul>
            {chosen.exclusionsNote && <p className="text-xs text-muted-foreground">{tr('billing', 'not_included_x', { text: chosen.exclusionsNote })}</p>}
          </div>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function CancelPackageDialog({ row, onClose, onDone }: { row: PatientPackageView; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [settlement, setSettlement] = useState<PackageCancelSettlement>('refund');
  const [error, setError] = useState<string | null>(null);
  const cancel = useMutation({
    mutationFn: () => post<PatientPackageView>(`/patient-packages/${row.id}/cancel`, { reason: reason.trim(), settlement }),
    onSuccess: () => {
      toast.success(tr('billing', 'package_cancelled'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const tooShort = reason.trim().length < 5;

  return (
    <Dialog
      open
      onClose={() => (cancel.isPending ? undefined : onClose())}
      title={tr('billing', 'cancel_package')}
      description={row.name}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={cancel.isPending}>
            {tr('billing', 'keep_package')}
          </Button>
          <Button type="button" variant="destructive" disabled={tooShort || cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? <Spinner /> : null}
            {tr('billing', 'cancel_package')}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <dl className="grid grid-cols-2 gap-2 rounded-md bg-muted/50 p-3">
          <dt className="text-muted-foreground">{tr('billing', 'package_price')}</dt>
          <dd className="text-end tabular">{money(row.saleNetMinor)}</dd>
          <dt className="text-muted-foreground">{tr('billing', 'used_at_list_prices')}</dt>
          <dd className="text-end tabular">{money(row.consumedValueMinor)}</dd>
          <dt className="font-medium">{tr('billing', 'to_credit_back')}</dt>
          <dd className="text-end tabular font-medium">{money(row.refundableMinor)}</dd>
        </dl>
        <p className="text-xs text-muted-foreground">{tr('billing', 'package_cancel_rule')}</p>
        {row.refundableMinor > 0 && (
          <Field label={tr('billing', 'what_to_do_with_the_credit')}>
            <ChoiceButtons<PackageCancelSettlement>
              layout="chips"
              value={settlement}
              onChange={setSettlement}
              options={[
                { value: 'refund', label: tr('billing', 'refund_what_was_paid') },
                { value: 'keep_credit', label: tr('billing', 'keep_it_as_credit') },
                { value: 'none', label: tr('billing', 'reduce_the_bill_only') },
              ]}
            />
          </Field>
        )}
        <Field label={tr('billing', 'reason')} required>
          <Textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
